/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from "@emotion/react";
import { Box, SvgIcon } from "@material-ui/core";
import { PCView, SPView } from "components/common/MediaQuery";
import { useI18n } from "hooks/i18n/i18n";
import React from "react";
import { colorTokens } from "theme/theme";

import { CommentItemSuperChatV2 } from "./CommentItemSuperChatV2";

import type { GiftItemData } from "modules/streaming";
import type { FC } from "react";

export const StreamingSelectGiftV2: React.FC<{
  isDarkModeEnabled: boolean;
  canPurchaseGiftItem: boolean;
  iconUrls: string | undefined;
  userName: string;
  comment: string;
  emoBalance: number;
  giftItems: GiftItemData[];
  selectedGiftItem: GiftItemData | null;
  canSendComment: boolean;
  onClickSendComment: () => void;
  onClickCloseGiftDrawer: () => void;
  onClickSelectedGift: (gift: GiftItemData) => void;
  onClickOpenPawChargeModal: () => void;
}> = ({
  isDarkModeEnabled,
  canPurchaseGiftItem,
  iconUrls,
  userName,
  comment,
  giftItems,
  emoBalance,
  selectedGiftItem,
  canSendComment,
  onClickSendComment,
  onClickCloseGiftDrawer,
  onClickSelectedGift,
  onClickOpenPawChargeModal,
}) => {
  const { t } = useI18n();

  return (
    <Box
      padding={["12px 8px", "0"]}
      border={["1px solid #eee", "none"]}
      borderRadius={["8px", "0"]}
    >
      <Box
        display={"grid"}
        gridTemplateColumns={"1fr max-content"}
        alignItems={"center"}
        marginBottom={"8px"}
      >
        <Box
          color={isDarkModeEnabled ? "dark.text.primary" : "light.text.primary"}
          fontSize={"14px"}
          fontWeight={"bold"}
        >
          {t("StreamingSelectGiftV2.sendGift")}
        </Box>

        <PCView>
          <Box style={{ cursor: "pointer" }} onClick={onClickCloseGiftDrawer}>
            <CrossIcon />
          </Box>
        </PCView>
      </Box>

      {/* ギフトプレビューを表示 */}
      {selectedGiftItem && (
        <Box marginBottom={"16px"}>
          <CommentItemSuperChatV2
            userIcon={iconUrls}
            userName={userName}
            isCommentUser
            isChatAdmin={false}
            amount={selectedGiftItem.values.paid ?? 0}
            message={{
              _id: "preview",
              uid: "dummy",
              msg:
                comment.length > 0
                  ? comment
                  : t("StreamingSelectGiftV2.commentPreview"),
            }}
            backgroundColor={selectedGiftItem.colorCode ?? "#000"}
            options={{
              color: selectedGiftItem.textColor ?? "#fff",
              stickerImageUrl: selectedGiftItem.imgUrl ?? "",
              backgroundImageUrl: selectedGiftItem.bgImgUrl ?? "",
            }}
            // 送信前のプレビューなのでメニューは出さない
            isMenuOpen={false}
            onOpenMenu={() => undefined}
            onCloseMenu={() => undefined}
          />
        </Box>
      )}

      {/* ギフト一覧を表示 */}
      <Box
        display={"flex"}
        flexWrap={"wrap"}
        gridGap={"6px"}
        marginBottom={"8px"}
      >
        {giftItems.map((item) => {
          const isSelected =
            selectedGiftItem && selectedGiftItem._id === item._id;
          return (
            <Box
              key={item._id}
              fontSize={"12px"}
              padding={"2px 8px"}
              color={
                isSelected
                  ? isDarkModeEnabled
                    ? "dark.text.secondary"
                    : "light.text.secondary"
                  : isDarkModeEnabled
                    ? "dark.text.tertiary"
                    : "light.text.tertiary"
              }
              bgcolor={
                isSelected
                  ? isDarkModeEnabled
                    ? "dark.action.primary"
                    : "light.action.primary"
                  : isDarkModeEnabled
                    ? "dark.action.secondary"
                    : "light.action.secondary"
              }
              border={"1px solid #A3A3A3"}
              borderColor={
                isSelected
                  ? isDarkModeEnabled
                    ? "dark.border.primary"
                    : "light.border.primary"
                  : isDarkModeEnabled
                    ? "dark.border.secondary"
                    : "light.border.secondary"
              }
              borderRadius={"6px"}
              style={{ cursor: "pointer" }}
              onClick={() => onClickSelectedGift(item)}
            >
              {item.name}
            </Box>
          );
        })}
      </Box>

      {/* 所有PAWと消費PAWを表示 */}
      <Box marginBottom={"8px"}>
        <Box display={"flex"} justifyContent={"end"}>
          <Box
            display={"grid"}
            gridTemplateColumns={"max-content 1fr"}
            gridGap={"8px"}
            alignItems={"center"}
            color={
              isDarkModeEnabled ? "dark.text.primary" : "light.text.primary"
            }
          >
            <Box fontSize={"12px"}>{t("StreamingSelectGiftV2.possession")}</Box>
            <Box
              display={"grid"}
              gridTemplateColumns={"max-content max-content"}
              alignItems={"center"}
              gridGap={"4px"}
            >
              <Box
                color={
                  canPurchaseGiftItem
                    ? isDarkModeEnabled
                      ? "dark.text.primary"
                      : "light.text.primary"
                    : "light.text.warning"
                }
                fontSize={"14px"}
                fontWeight={"bold"}
              >
                {emoBalance.toLocaleString()}
              </Box>
              <Box fontSize={"12px"}>PAW</Box>
            </Box>
          </Box>
        </Box>

        <Box display={"flex"} justifyContent={"end"}>
          <Box
            display={"grid"}
            gridTemplateColumns={"max-content 1fr"}
            alignItems={"end"}
            gridGap={"8px"}
            color={
              isDarkModeEnabled ? "dark.text.primary" : "light.text.primary"
            }
          >
            <Box fontSize={"12px"}>{t("StreamingSelectGiftV2.use")}</Box>
            <Box
              display={"grid"}
              gridTemplateColumns={"max-content max-content"}
              alignItems={"center"}
              gridGap={"4px"}
            >
              <Box
                color={"light.text.highlight"}
                fontSize={"14px"}
                fontWeight={"bold"}
              >
                {selectedGiftItem
                  ? selectedGiftItem.values.paid.toLocaleString()
                  : "0"}
              </Box>
              <Box fontSize={"12px"}>PAW</Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <SPView>
        <Box display={"flex"} justifyContent={"end"} gridGap={"8px"}>
          <StreamingButton
            color={
              isDarkModeEnabled
                ? colorTokens.colors["text.primary"].dark
                : colorTokens.colors["text.primary"].default
            }
            bgcolor={
              isDarkModeEnabled
                ? colorTokens.colors["action.secondary"].dark
                : colorTokens.colors["action.secondary"].default
            }
            border={
              isDarkModeEnabled
                ? colorTokens.colors["border.primary"].dark
                : colorTokens.colors["border.primary"].default
            }
            onClick={onClickCloseGiftDrawer}
          >
            {t("streaming.gift.cancel")}
          </StreamingButton>

          {canPurchaseGiftItem ? (
            <StreamingButton
              color={
                isDarkModeEnabled
                  ? colorTokens.colors["text.secondary"].dark
                  : colorTokens.colors["text.secondary"].default
              }
              bgcolor={
                isDarkModeEnabled
                  ? colorTokens.colors["action.primary"].dark
                  : colorTokens.colors["action.primary"].default
              }
              border={
                isDarkModeEnabled
                  ? colorTokens.colors["border.primary"].dark
                  : colorTokens.colors["border.primary"].default
              }
              disabled={!canSendComment}
              onClick={onClickSendComment}
            >
              {t("streaming.gift.purchaseAndSend")}
            </StreamingButton>
          ) : (
            <StreamingButton
              color={
                isDarkModeEnabled
                  ? colorTokens.colors["text.secondary"].dark
                  : colorTokens.colors["text.secondary"].default
              }
              bgcolor={
                isDarkModeEnabled
                  ? colorTokens.colors["action.primary"].dark
                  : colorTokens.colors["action.primary"].default
              }
              border={
                isDarkModeEnabled
                  ? colorTokens.colors["border.primary"].dark
                  : colorTokens.colors["border.primary"].default
              }
              disabled={!canSendComment}
              onClick={onClickOpenPawChargeModal}
            >
              {t("streaming.gift.PAWCharge")}
            </StreamingButton>
          )}
        </Box>
      </SPView>
    </Box>
  );
};

const StreamingButton: FC<{
  children: React.ReactNode | string;
  color: string;
  bgcolor: string;
  border: string;
  disabled?: boolean;
  onClick: () => void;
}> = ({ children, color, bgcolor, border, disabled = false, onClick }) => {
  const { t } = useI18n();

  return (
    <button
      aria-label={t("streaming.comment.sendAriaLabel")}
      disabled={disabled}
      css={css`
        display: flex;
        align-items: center;
        padding: 7px 10px;
        color: ${color};
        font-size: 12px;
        background-color: ${bgcolor};
        border-radius: 6px;
        border: 1px solid ${border};
        cursor: pointer;
        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

const CrossIcon = () => {
  return (
    <SvgIcon viewBox="0 0 24 24">
      <g transform="translate(6,6)">
        <path
          d="M5 3.88906L8.88906 0L10 1.11094L6.11094 5L10 8.88906L8.88906 10L5 6.11094L1.11094 10L0 8.88906L3.88906 5L0 1.11094L1.11094 0L5 3.88906Z"
          fill="#A3A3A3"
        />
      </g>
    </SvgIcon>
  );
};
